import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { Figure } from '@/pages/ibama/OverviewPage';
import { fetchIbamaNotifications } from '@/services/ibama';

/**
 * IBAMA's notification register, in counts.
 *
 * Most rows were only entered in the system and never printed, so they
 * carry no date, no state and usually no document; the register shape
 * says so as its first figure and every by-year table covers the dated
 * minority. No notification is ever listed: most name a natural person or
 * nobody. The escalation share is a fact about the register, not a
 * probability.
 */
export function IbamaNotificationsPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'notifications'],
    queryFn: fetchIbamaNotifications,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:notifications.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:notifications.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:notifications.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path="/api/v1/ibama/export/notifications"
          label={t('ibama:notifications.export')}
        />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-register>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:notifications.register.title')}
        </h2>
        <Answer answer={data.shape} help={t('ibama:notifications.register.help')}>
          {(s) => (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Figure
                label={t('ibama:notifications.register.rows')}
                value={number.format(s.rows)}
              />
              <Figure
                label={t('ibama:notifications.register.dated')}
                value={`${number.format(s.dated)} (${percent.format(s.dated_share)})`}
              />
              <Figure
                label={t('ibama:notifications.register.autoIssued')}
                value={`${number.format(s.auto_issued)} (${percent.format(s.auto_issued_share)})`}
              />
              <Figure
                label={t('ibama:notifications.register.linked')}
                value={number.format(s.linked_to_process)}
              />
              <Figure
                label={t('ibama:notifications.register.companies')}
                value={number.format(s.companies)}
              />
              <Figure
                label={t('ibama:notifications.register.persons')}
                value={number.format(s.persons)}
              />
              <Figure
                label={t('ibama:notifications.register.noSubject')}
                value={number.format(s.no_subject)}
              />
              <Figure
                label={t('ibama:notifications.register.cancelled')}
                value={number.format(s.cancelled)}
              />
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ibama:notifications.guide" />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={{ ...data.shape, value: data.shape.value?.statuses ?? null }}
          titleKey="ibama:notifications.statuses.title"
          helpKey="ibama:notifications.statuses.help"
          labelPrefix="ibama:notifications.statusCodes."
        />
        <OutcomeMixPanel
          answer={{ ...data.attendance, value: data.attendance.value?.combinations ?? null }}
          titleKey="ibama:notifications.attendance.title"
          helpKey="ibama:notifications.attendance.help"
          labelPrefix="ibama:notifications.attendanceCodes."
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-by-year>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:notifications.years.title')}
          </h2>
          <Answer answer={data.by_year} help={t('ibama:notifications.years.help')}>
            {(rows) => (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('ibama:notifications.years.year')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:notifications.years.rows')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:notifications.years.autoIssued')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:notifications.years.companies')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((y) => (
                      <tr key={y.year} className="border-t" data-year={y.year}>
                        <td className="px-3 py-2 tabular-nums text-foreground">{y.year}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(y.rows)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(y.auto_issued)}
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({percent.format(y.auto_issued_share)})
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(y.companies)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-by-uf>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:notifications.states.title')}
          </h2>
          <Answer answer={data.by_uf} help={t('ibama:notifications.states.help')}>
            {(rows) => (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('ibama:notifications.states.uf')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:notifications.states.rows')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:notifications.states.autoIssued')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((u) => (
                      <tr key={u.uf} className="border-t" data-uf={u.uf}>
                        <td className="px-3 py-2 text-foreground">{u.uf}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(u.rows)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(u.auto_issued)}
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({percent.format(u.auto_issued_share)})
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Answer>
        </section>
      </div>
    </div>
  );
}
